import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';
import { cls } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import { useInputLogic } from '../input/hooks';

/**
 * 多行文本输入框组件，支持自适应内容高度。
 * @en A multi-line textarea, supports adaptive content height.
 * @type 数据输入
 * @type_en Data Entry
 * @name 多行文本框
 * @name_en Textarea
 */
var Textarea = /*#__PURE__*/forwardRef(function (props, ref) {
  var id = props.id,
      name = props.name,
      rows = props.rows,
      placeholder = props.placeholder,
      readOnly = props.readOnly,
      onKeyUp = props.onKeyUp,
      onKeyPress = props.onKeyPress,
      disabled = props.disabled,
      autosize = props.autosize,
      cloneNodeWhenAutosize = props.cloneNodeWhenAutosize,
      _props$showStatistics = props.showStatistics,
      showStatistics = _props$showStatistics === void 0 ? true : _props$showStatistics,
      statisticsMaxlength = props.statisticsMaxlength,
      statisticsLengthCaculator = props.statisticsLengthCaculator,
      onErrStatusChange = props.onErrStatusChange,
      onErrValueChange = props.onErrValueChange,
      renderStatistics = props.renderStatistics,
      maxLength = props.maxLength,
      textareaClass = props.textareaClass,
      textareaStyle = props.textareaStyle,
      _props$nativeProps = props.nativeProps,
      nativeProps = _props$nativeProps === void 0 ? {} : _props$nativeProps;
  var textareaRef = useRef(null);
  var cloneNodeRef = useRef(null);

  var _useInputLogic = useInputLogic(props, textareaRef),
      inputValue = _useInputLogic.inputValue,
      handleClick = _useInputLogic.handleClick,
      handleChange = _useInputLogic.handleChange,
      handleInput = _useInputLogic.handleInput,
      handleKeyDown = _useInputLogic.handleKeyDown,
      handleFocus = _useInputLogic.handleFocus,
      handleBlur = _useInputLogic.handleBlur,
      renderWrapper = _useInputLogic.renderWrapper,
      wrapRef = _useInputLogic.wrapRef;

  var maxValueLength = statisticsMaxlength || maxLength;
  var currentValueLength = useMemo(function () {
    if (statisticsLengthCaculator) {
      return statisticsLengthCaculator(inputValue);
    } // 默认为过滤换行后的字符长度
    // @en The default is the character length after filtering newlines


    var validValue = inputValue.replace(/\n/g, '');
    return validValue ? String(validValue).length : 0;
  }, [inputValue, statisticsLengthCaculator]);
  var exceed = maxValueLength ? currentValueLength > maxValueLength : false;
  useImperativeHandle(ref, function () {
    return {
      dom: wrapRef.current,
      textarea: textareaRef.current,
      resize: resizeTextarea
    };
  }, [resizeTextarea]);
  useEffect(function () {
    return function () {
      cloneNodeRef.current && document.body.removeChild(cloneNodeRef.current);
    };
  }, []);
  useEffect(function () {
    resizeTextarea();
  }, [inputValue, placeholder]);
  useEffect(function () {
    onErrStatusChange && onErrStatusChange(exceed);
  }, [exceed]);
  useEffect(function () {
    onErrValueChange && onErrValueChange(exceed, currentValueLength, maxValueLength);
  }, [maxValueLength, currentValueLength]);

  function resizeTextarea() {
    if (!autosize || !textareaRef.current) {
      return;
    }

    var currentRef = textareaRef.current;

    if (cloneNodeWhenAutosize) {
      var cloneNode = textareaRef.current.cloneNode(true);

      if (cloneNodeRef.current) {
        document.body.replaceChild(cloneNode, cloneNodeRef.current);
      } else {
        document.body.appendChild(cloneNode);
      }

      var computedStyle = window.getComputedStyle(textareaRef.current); // 影响布局的css属性复制
      // @en Copy css properties which affect layout

      ['width', 'fontSize', 'lineHeight', 'letterSpacing', 'wordSpacing'].forEach(function (styleKey) {
        cloneNode.style[styleKey] = computedStyle[styleKey];
      });
      cloneNode.classList.add('fake-textarea');
      cloneNodeRef.current = cloneNode;
      currentRef = cloneNode;
    }

    currentRef.style.height = 'auto';
    var maxHeight = Math.max(currentRef.clientHeight, currentRef.scrollHeight);
    textareaRef.current.style.height = maxHeight + "px";
  }

  function renderTextarea(_ref) {
    var prefixCls = _ref.prefixCls;
    var prefix = prefixCls + "-input";
    return renderWrapper(prefix, "textarea " + (showStatistics ? 'has-stat' : ''), /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("textarea", _extends({}, nativeProps, {
      id: id,
      name: name,
      rows: rows,
      placeholder: placeholder,
      readOnly: readOnly,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyUp: onKeyUp,
      onKeyPress: onKeyPress,
      ref: textareaRef,
      className: cls(prefixCls + "-textarea", textareaClass),
      style: textareaStyle,
      value: inputValue,
      maxLength: maxLength,
      disabled: disabled,
      onChange: handleChange,
      onInput: handleInput,
      onKeyDown: handleKeyDown,
      onClick: handleClick
    })), showStatistics && maxValueLength ? /*#__PURE__*/React.createElement("span", {
      className: cls('statistic-text', {
        exceed: exceed
      })
    }, renderStatistics ? renderStatistics(currentValueLength, maxValueLength) : currentValueLength + "/" + maxValueLength) : null));
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, renderTextarea);
});
export default Textarea;